/**
 *      ▄ █
 *    █ █ ▀▀█  LOADBALANCER CLIENT
 *    █ ▀ ▀ █  @copyright Copyright (c) 2025
 */
import { BaseComponent } from '../../../../../core/components';
import { Component } from '@angular/core';
import { DropdownModel, ErrorResponseModel } from '../../../../../core/models';
import { FormBuilder, FormGroup, Validators } from '@angular/forms';
import { LogService, RbacService } from '../../../../../core/services';
import { Observable } from 'rxjs';
import { Router } from '@angular/router';
import { SupportApiService } from '../../../shared/http';
import { takeUntil } from 'rxjs/operators';

@Component({
    selector: 'app-ticket-create',
    templateUrl: './ticket-create.component.html',
    styleUrls: ['./ticket-create.component.scss'],
})

export class TicketCreateComponent extends BaseComponent {
    /**
     * To store the ticket.
     */
    public ticketForm: FormGroup;

    /**
     * Editor styles.
     */
    public editorStyles: any = {
        width: '700px',
        height: '200px'
    };

    /**
     * Dropdown content.
     */
    public dropdown: any = {
        type: [
            <DropdownModel>{ key: 'problem', value: 'Problem' },
            <DropdownModel>{ key: 'incident', value: 'Incident' },
            <DropdownModel>{ key: 'question', value: 'Question' },
            <DropdownModel>{ key: 'task', value: 'Task' }
        ],
        product: [
            <DropdownModel>{ key: 'product_portal', value: 'Portal' },
            <DropdownModel>{ key: 'product_endurance', value: 'Endurance' },
            <DropdownModel>{ key: 'product_enterprise', value: 'Enterprise' }
        ],
        priority: [
            <DropdownModel>{ key: 'normal', value: 'Normal' }
        ]
    };

    /**
     * Store permissions in lookup table.
     */
    public permissionsLookup: any = {};

    constructor(
        private formBuilder: FormBuilder,
        private logService: LogService,
        private rbacService: RbacService,
        private router: Router,
        private supportApiService: SupportApiService,
    ) {
        super();

        this.setUpForm();
        this.registerButtons();
    }

    /**
     * Submit ticket.
     */
    public submitTicket(): Observable<any> {
        return new Observable<any>(observe => {
            const data = this.ticketForm.value;

            data['comment'] = {
                // eslint-disable-next-line camelcase
                html_body: this.ticketForm.value.comment,
                uploads: ['']
            };

            this.supportApiService.createTicket(data)
                .pipe(takeUntil(this.destroyed$))
                .subscribe({
                    next: (data: any): void => {
                        this.router.navigate(['cm/support/conversation/' + data.ticket.id]).then();
                        observe.next();
                        observe.complete();
                    },
                    error: (error: ErrorResponseModel): void => {
                        this.logService.error(
                            'TicketConversationComponent:updateTicket',
                            'Error updating the ticket' + error
                        );
                        observe.error(error);
                        observe.complete();
                    }
                });
        });
    }

    /**
     * Set up the form for the page
     * @private
     */
    private setUpForm(): void {
        this.ticketForm = this.formBuilder.group({
            type: ['incident', Validators.required],
            product: ['product_portal', Validators.required],
            priority: ['normal', Validators.required],
            subject: [null, Validators.required],
            comment: [null, Validators.required]
        });
    }

    /**
     * Register the button permissions for the page.
     * @private
     */
    private registerButtons(): void {
        const buttonReference = {
            'ticket-create-submit': [
                { path: '/api/v1/support/tickets/', methods: ['POST'] },
                { path: '/api/v1/support/tickets/:id', methods: ['GET'] },
                { path: '/api/v1/support/tickets/:id/comments/', methods: ['GET'] }
            ],
        };

        this.permissionsLookup = this.rbacService.getPermissionsLookup(buttonReference);
    }
}
